import { Box, Section } from "@vinomofo/components";
import { OfferCard, OfferTimer } from "../offer-card/OfferCard";
import { memo, useMemo } from "react";

import { BadgeStack } from "../badge-stack";
import { MarqueeOfferHeroImage } from "../offer-hero/MarqueeOfferHeroImage";
import { OfferHeroCard } from "../offer-hero/OfferHeroCard";
import isEqual from "lodash.isequal";
import { isFlagEnabled } from "../../utils/is-flag-enabled";
import { offerCardPropsFromVinoDataOffer } from "../../transformers/vino-data/offer-card";
import styled from "styled-components";

const MarqueeOffer = styled(Box)<{
  width: string;
  float: string;
}>`
  background: white;
  border-radius: 5px;
  min-height: 470px;
  width: ${({ width }) => (!width ? "50%" : width)};
  float: ${({ float }) => (!float ? "none" : float)};
  ${({ float }) =>
    !float && {
      marginLeft: "auto",
      marginRight: "auto",
    }}
  position: relative;
`;

const MarqueeOfferWrap = styled(Box)<{
  columns: number;
  pt: string;
}>`
  padding: 20px 20px 20px;
  padding-top: ${({ pt }) => pt || "20px"};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 20px;
  align-items: center;
  position: relative;
`;

const MarqueeOfferBottle = styled(Box)`
  width: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const MarqueeOfferDetails = styled(Box)`
  background: #fff;
  width: 50%;
`;

const BadgeContainer = styled(Box)`
  transform: scale(0.6);
`;

const MarqueeOfferTimer = styled(OfferTimer)`
  top: -17px;
  left: 10px;
  width: calc(100% - 20px);
`;

interface MQOfferProps {
  rawOffer: any;
  locationHref: string;
  details: any;
  isExpired: boolean;
  endsAt: string;
  pt: string | number;
}

const MQOffer: React.FC<MQOfferProps> = memo(
  ({ rawOffer, locationHref, details, isExpired, endsAt, pt }) => {
    const offer = useMemo(() => ({ ...rawOffer, url: locationHref }), [
      rawOffer,
      locationHref,
    ]);
    const offerCardProps = useMemo(
      () => offerCardPropsFromVinoDataOffer(offer),
      [offer]
    );

    const {
      productId,
      price,
      unitRRP,
      priceDescription,
      shortDescription,
      discountPercentage,
      region,
      style,
      showPriceDrop,
      awards,
      cardImage,
      showDiscountBadge,
      isSoldOut,
      name,
      roundelsImage,
      urgencyMessage,
    } = details;

    return (
      <Section zIndex={1} marginTop={0} paddingTop={pt} key={offer.id}>
        {/* MOBILE */}
        <MarqueeOffer
          display={["block", "none", "none"]}
          marginTop={[0, 0, 30]}
          width="80%"
        >
          <OfferCard
            {...offerCardProps}
            offerJson={offer}
            skeleton={false}
            height="100%"
            variant="responsive"
            eventPage={true}
            showCountdown={isFlagEnabled(offer.displayFlags, "SHOW_COUNTDOWN")}
            disableAllDisplayFlags={true}
            showAwardBadges={!rawOffer?.hidden}
          />
        </MarqueeOffer>

        {/* TABLET */}
        <MarqueeOffer
          display={["none", "block", "none"]}
          marginTop={[0, 0, 30]}
          width="50%"
          float="right"
        >
          <OfferCard
            {...offerCardProps}
            offerJson={offer}
            skeleton={false}
            height="100%"
            variant="responsive"
            eventPage={true}
            showCountdown={isFlagEnabled(offer.displayFlags, "SHOW_COUNTDOWN")}
            disableAllDisplayFlags={true}
            showAwardBadges={!rawOffer?.hidden}
          />
        </MarqueeOffer>

        {/* DESKTOP */}
        <MarqueeOffer
          display={["none", "none", "block"]}
          marginTop={[0, 0, 30]}
          width="60%"
          float="right"
        >
          {endsAt && !isExpired && (
            <MarqueeOfferTimer
              isExpired={isExpired}
              end={new Date(endsAt)}
              countDays={false}
              height="34px"
            />
          )}
          <MarqueeOfferWrap pt={endsAt && !isExpired ? "50px" : "20px"}>
            <MarqueeOfferBottle>
              <MarqueeOfferHeroImage
                image={cardImage}
                roundelsImage={roundelsImage}
                discountPercent={discountPercentage}
                showDiscountBadge={showDiscountBadge}
                isAvailable={true}
                display={["block", "block", "block"]}
                marginTop={["0", "15px !important", "0"]}
              />
              {rawOffer?.hidden ||
                (!rawOffer?.isMixed && (
                  <BadgeContainer>
                    <BadgeStack
                      badges={awards}
                      limit={5}
                      horizontal={[false, true]}
                      gap={["-10px", "10px", "-20px"]}
                      height={100}
                      justifyContent={"center"}
                    />
                  </BadgeContainer>
                ))}
            </MarqueeOfferBottle>
            <MarqueeOfferDetails>
              <OfferHeroCard
                productId={productId}
                shortDescription={shortDescription}
                price={price}
                rrp={unitRRP}
                priceDescription={priceDescription}
                region={region}
                style={style}
                endsAt={endsAt}
                showPriceDrop={showPriceDrop}
                urgencyMessage={urgencyMessage}
                isSoldOut={isSoldOut}
                name={name}
                offer={offer}
                showCountdown={false}
                hideIconStack
                showPaymentOptions={false}
                disableAllDisplayFlags={true}
                isMarquee={true}
              />
            </MarqueeOfferDetails>
          </MarqueeOfferWrap>
        </MarqueeOffer>
      </Section>
    );
  },
  isEqual
);

export default MQOffer;
